import React from 'react';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { findTable } from '@atlaskit/editor-tables';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { useInternalTablePluginStateSelector } from '../ui/hooks/useInternalTablePluginStateSelector';
import TableComponent from './TableComponent';

// Ignored via go/ees005
// eslint-disable-next-line @typescript-eslint/no-explicit-any

/**
 * Use useSharedPluginState to control re-renders from plugin dependencies
 */
export var TableComponentWithSharedState = function TableComponentWithSharedState(_ref) {
  var view = _ref.view,
    options = _ref.options,
    getNode = _ref.getNode,
    dispatchAnalyticsEvent = _ref.dispatchAnalyticsEvent,
    api = _ref.api,
    getEditorFeatureFlags = _ref.getEditorFeatureFlags,
    eventDispatcher = _ref.eventDispatcher,
    allowColumnResizing = _ref.allowColumnResizing,
    allowControls = _ref.allowControls,
    getPos = _ref.getPos,
    forwardRef = _ref.forwardRef,
    allowTableAlignment = _ref.allowTableAlignment,
    allowTableResizing = _ref.allowTableResizing;
  var _useSharedPluginState = useSharedPluginState(api, ['width', 'table', 'media', 'selection', 'editorViewMode'], {
      disabled: editorExperiment('platform_editor_usesharedpluginstateselector', true)
    }),
    widthState = _useSharedPluginState.widthState,
    tableState = _useSharedPluginState.tableState,
    mediaState = _useSharedPluginState.mediaState,
    selectionState = _useSharedPluginState.selectionState,
    editorViewModeState = _useSharedPluginState.editorViewModeState;
  var isTableResizingSelector = useInternalTablePluginStateSelector(api, 'isTableResizing', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isHeaderColumnEnabledSelector = useInternalTablePluginStateSelector(api, 'isHeaderColumnEnabled', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isHeaderRowEnabledSelector = useInternalTablePluginStateSelector(api, 'isHeaderRowEnabled', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var orderingSelector = useInternalTablePluginStateSelector(api, 'ordering', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isResizingSelector = useInternalTablePluginStateSelector(api, 'isResizing', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isInDangerSelector = useInternalTablePluginStateSelector(api, 'isInDanger', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var hoveredCellSelector = useInternalTablePluginStateSelector(api, 'hoveredCell', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var hoveredRowsSelector = useInternalTablePluginStateSelector(api, 'hoveredRows', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isTableHoveredSelector = useInternalTablePluginStateSelector(api, 'isTableHovered', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isWholeTableInDangerSelector = useInternalTablePluginStateSelector(api, 'isWholeTableInDanger', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });

  // mediaState
  var isFullscreenSelector = useSharedPluginStateSelector(api, 'media.isFullscreen', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isFullscreen = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? isFullscreenSelector : mediaState === null || mediaState === void 0 ? void 0 : mediaState.isFullscreen;

  // selectionState
  var selectionSelector = useSharedPluginStateSelector(api, 'selection.selection', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var selection = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? selectionSelector : selectionState === null || selectionState === void 0 ? void 0 : selectionState.selection;

  // editorViewModeState
  var editorViewModeSelector = useSharedPluginStateSelector(api, 'editorViewMode.mode', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var mode = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? editorViewModeSelector : editorViewModeState === null || editorViewModeState === void 0 ? void 0 : editorViewModeState.mode;
  var isLivePageViewMode = mode === 'view';

  // widthState
  var widthSelector = useSharedPluginStateSelector(api, 'width.width', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var width = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? widthSelector : widthState === null || widthState === void 0 ? void 0 : widthState.width;
  var lineLengthSelector = useSharedPluginStateSelector(api, 'width.lineLength', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var lineLength = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? lineLengthSelector : widthState === null || widthState === void 0 ? void 0 : widthState.lineLength;
  if (editorExperiment('platform_editor_usesharedpluginstateselector', false) && !tableState) {
    return null;
  }

  // tableState
  var isTableResizing = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? isTableResizingSelector : tableState.isTableResizing;
  var isHeaderColumnEnabled = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? isHeaderColumnEnabledSelector : tableState.isHeaderColumnEnabled;
  var isHeaderRowEnabled = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? isHeaderRowEnabledSelector : tableState.isHeaderRowEnabled;
  var ordering = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? orderingSelector : tableState.ordering;
  var isResizing = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? isResizingSelector : tableState.isResizing;
  var isInDanger = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? isInDangerSelector : tableState.isInDanger;
  var hoveredCell = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? hoveredCellSelector : tableState.hoveredCell;
  var hoveredRows = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? hoveredRowsSelector : tableState.hoveredRows;
  var isTableHovered = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? isTableHoveredSelector : tableState.isTableHovered;
  var isWholeTableInDanger = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? isWholeTableInDangerSelector : tableState.isWholeTableInDanger;

  /**
   *  ED-19810
   *  There is a getPos issue coming from this code. We need to apply this workaround for now and apply a patch
   *  directly to confluence since this bug is now in production.
   */
  var currentTablePos;
  try {
    currentTablePos = getPos ? getPos() : undefined;
  } catch (e) {
    currentTablePos = undefined;
  }
  var selectedTable = findTable(view.state.selection);
  var tablePos = selectedTable && selectedTable.start - 1;
  var tableActive = typeof currentTablePos === 'number' && typeof tablePos === 'number' && currentTablePos === tablePos && !isTableResizing;
  return /*#__PURE__*/React.createElement(TableComponent, {
    view: view,
    allowColumnResizing: allowColumnResizing,
    eventDispatcher: eventDispatcher,
    getPos: getPos,
    isMediaFullscreen: isFullscreen,
    options: options,
    allowControls: allowControls,
    isHeaderRowEnabled: isHeaderRowEnabled !== null && isHeaderRowEnabled !== void 0 ? isHeaderRowEnabled : false,
    isHeaderColumnEnabled: isHeaderColumnEnabled !== null && isHeaderColumnEnabled !== void 0 ? isHeaderColumnEnabled : false,
    isDragAndDropEnabled: (options === null || options === void 0 ? void 0 : options.isDragAndDropEnabled) && !isLivePageViewMode,
    isTableScalingEnabled: options === null || options === void 0 ? void 0 : options.isTableScalingEnabled,
    allowTableAlignment: allowTableAlignment,
    allowTableResizing: allowTableResizing,
    tableActive: tableActive && !isLivePageViewMode,
    ordering: ordering,
    isResizing: isResizing,
    getNode: getNode,
    containerWidth: editorExperiment('platform_editor_usesharedpluginstateselector', true) ? {
      width: width !== null && width !== void 0 ? width : 0,
      lineLength: lineLength
    } :
    // Ignored via go/ees005
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    widthState,
    contentDOM: forwardRef,
    getEditorFeatureFlags: getEditorFeatureFlags,
    dispatchAnalyticsEvent: dispatchAnalyticsEvent,
    pluginInjectionApi: api,
    isInDanger: !!isInDanger,
    hoveredRows: hoveredRows,
    hoveredCell: hoveredCell,
    isTableHovered: isTableHovered,
    isWholeTableInDanger: isWholeTableInDanger,
    selection: selection
  });
};